import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUSES } from "@/lib/clients";
import { formatPhoneInput } from "@/lib/clients";
import { TagEditor } from "@/components/TagEditor";
import { GroupSelect } from "@/components/GroupSelect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", company: "", status: "lead", address: "", tags: [], group_ids: [], assigned_user_ids: [] };
const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

function Field({ label, testid, value, onChange, required }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input data-testid={testid} required={required} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    </div>
  );
}

export default function ClientFormDialog({ open, onOpenChange, client, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const isEdit = Boolean(client);

  useEffect(() => {
    if (open) {
      setForm(client ? { ...empty, ...client } : empty);
      api.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups([]));
      if (isAdmin) api.get("/users").then((r) => setMembers(r.data)).catch(() => setMembers([]));
    }
  }, [open, client, isAdmin]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const phoneDigits = String(form.phone || "").replace(/\D/g, "");
    if (phoneDigits && phoneDigits.length !== 10) {
      toast.error("Phone number must have exactly 10 digits");
      return;
    }
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error("Enter a valid email address (e.g. name@example.com)");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/clients/${client.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">
            {isEdit ? "Edit client" : "New client"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <Field label="Name *" testid="client-name-input" value={form.name} onChange={(v) => set("name", v)} required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" testid="client-email-input" value={form.email} onChange={(v) => set("email", v)} />
            <Field label="Phone" testid="client-phone-input" value={form.phone} onChange={(v) => set("phone", formatPhoneInput(v))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company" testid="client-company-input" value={form.company} onChange={(v) => set("company", v)} />
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="client-status-select" value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <Field label="Address" testid="client-address-input" value={form.address} onChange={(v) => set("address", v)} />
          <TagEditor tags={form.tags} onChange={(tags) => set("tags", tags)} />
          <div>
            <label className={labelCls}>Access groups <span className="font-normal text-gray-400">(empty = visible to everyone)</span></label>
            <GroupSelect groups={groups} selected={form.group_ids || []} onChange={(ids) => set("group_ids", ids)} testidPrefix="client-group" />
          </div>
          {isAdmin && (
            <div>
              <label className={labelCls}>Assigned team members <span className="font-normal text-gray-400">(restricts access)</span></label>
              <GroupSelect groups={members} selected={form.assigned_user_ids || []} onChange={(ids) => set("assigned_user_ids", ids)} testidPrefix="client-assignee" />
              <p className="text-xs text-gray-400 mt-1.5">
                {(form.assigned_user_ids || []).length > 0
                  ? "Only the selected members and admins can access this client."
                  : "No one assigned — access falls back to the access groups above."}
              </p>
            </div>
          )}
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="client-save-button"
              className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save changes" : "Add client"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
